<template>
  <div v-if="subscriptionId && available" class="member-guarantee mt-4">
    <h4 class="benefits-title">
      <i class="fas fa-shield-alt mr-2"></i>
      Garantía de 30 días
    </h4>
    <b-spinner v-if="loading" small class="my-2" />
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">
      {{ errorMessage }}
    </b-alert>
    <div v-if="info">
      <p v-if="eligible" class="mb-2">
        Puedes pedir el reembolso de todo lo pagado hasta el {{ deadlineLabel }}.
      </p>
      <p v-else class="mb-2">{{ ineligibleMessage }}</p>
      <ul v-if="usedBenefits.length" class="small mb-2">
        <li v-for="(item, index) in usedBenefits" :key="index">{{ benefitLabel(item) }}</li>
      </ul>
      <div v-if="request" class="small text-muted mb-2">
        Última solicitud: {{ request.status || '—' }}
        <span v-if="request.requested_at"> · {{ request.requested_at }}</span>
        <span v-if="request.refunded_amount != null"> · reembolso ${{ request.refunded_amount }}</span>
        <span v-if="request.review_notes"> · {{ request.review_notes }}</span>
      </div>
      <div v-if="canRequest">
        <b-form-textarea v-model="reason" rows="2" max-rows="4" placeholder="Motivo (opcional)" class="mb-2" />
        <b-button variant="outline-primary" :disabled="saving" @click="submit">
          {{ saving ? 'Enviando...' : 'Solicitar garantía' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BAlert, BButton, BFormTextarea, BSpinner } from 'bootstrap-vue'
import memberService from '@/core/services/api/member'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import { GUARANTEE_REASON_COPY, unwrapRecord } from '@/core/utils/membershipFamily'

/**
 * Garantía de 30 días. Solo la ve quien paga la suscripción.
 */
export default {
  name: 'MemberGuaranteeCard',
  components: { BAlert, BButton, BFormTextarea, BSpinner },
  props: {
    subscriptionId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      available: true,
      errorMessage: null,
      info: null,
      reason: '',
    }
  },
  computed: {
    eligibility() {
      return (this.info && this.info.eligibility) || {}
    },
    eligible() {
      return this.eligibility.eligible === true
    },
    request() {
      return this.info && this.info.request
    },
    usedBenefits() {
      const list = this.eligibility.used_benefits
      return Array.isArray(list) ? list : []
    },
    deadlineLabel() {
      return this.eligibility.deadline || 'la fecha límite'
    },
    ineligibleMessage() {
      const reason = this.eligibility.reason
      if (this.eligibility.message) return this.eligibility.message
      return GUARANTEE_REASON_COPY[reason] || 'Este plan no puede pedir la garantía ahora.'
    },
    canRequest() {
      if (!this.eligible) return false
      if (!this.request) return true
      return this.request.status === 'rejected'
    },
  },
  watch: {
    subscriptionId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    benefitLabel(item) {
      if (!item) return ''
      if (typeof item === 'string') return item
      return item.name || item.label || item.type || 'Beneficio usado'
    },
    async load() {
      if (!this.subscriptionId) {
        this.info = null
        return
      }
      this.loading = true
      this.errorMessage = null
      try {
        const response = await memberService.getGuarantee(this.subscriptionId)
        this.info = unwrapRecord(response)
      } catch (error) {
        this.info = null
        const status = error && error.response && error.response.status
        if (status === 404) {
          this.available = false
          return
        }
        if (status !== 403) {
          this.errorMessage = membershipApiMessage(error, 'No se pudo consultar la garantía.')
        }
      } finally {
        this.loading = false
      }
    },
    async submit() {
      this.saving = true
      this.errorMessage = null
      try {
        const response = await memberService.requestGuarantee(this.subscriptionId, this.reason)
        this.info = unwrapRecord(response)
        this.reason = ''
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo solicitar la garantía.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
